/**
 * The History card's header (PRD-0075 R-C1, R-C2, SC-010, SC-011): when the case first and last
 * saw the entity, from how many sources, its mentions per month, the last change and from which
 * source, and every property whose current value came in over another source's value. The change
 * badge shows while a merge or property change is younger than 30 days.
 *
 * Presentational: given the view `features/entity-history` makes of the log's summary.
 */
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CaseEventKind } from '@/services/api/case-events'
import { formatDate } from '@/utils/date'
import { type ActivityMonth, ActivitySparkline } from './ActivitySparkline'
import { ChangedBadge } from './ChangedBadge'

const HEADER = 'investigations.entityDetail.history.header'

/** A property value as the header prints it; null when there was none. */
type Value = string | null

export type LastChangeView =
  | {
      kind: 'property_changed'
      key: string
      old: Value
      new: Value
      documentName: string | null
      learnedAt: string
      /** An analyst's pin kept `new` out; `old` stays (ADR-0093). */
      pinned?: boolean
    }
  | { kind: Exclude<CaseEventKind, 'property_changed'> | null; learnedAt: string }

export interface PropertyConflictView {
  key: string
  value: Value
  previous: Value
  documentName: string | null
  previousDocumentName: string | null
  /** `value` is an analyst's assertion; `previous` the source's value its pin kept out. */
  pinned?: boolean
}

export interface EntityHistoryHeaderView {
  firstSeenAt: string | null
  lastSeenAt: string | null
  sourceCount: number
  /** Oldest first, empty months filled in. */
  activity: readonly ActivityMonth[]
  lastChange: LastChangeView | null
  conflicts: readonly PropertyConflictView[]
  /** A merge or property change younger than 30 days (R-C2). */
  changed: boolean
}

function Seen({ view }: { view: EntityHistoryHeaderView }) {
  const { t, i18n } = useTranslation()
  const date = (iso: string) => formatDate(iso, i18n.language)
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
      {view.firstSeenAt && (
        <span>{t(`${HEADER}.firstSeen`, { date: date(view.firstSeenAt) })}</span>
      )}
      {view.lastSeenAt && <span>{t(`${HEADER}.lastSeen`, { date: date(view.lastSeenAt) })}</span>}
      <span>{t(`${HEADER}.sources`, { count: view.sourceCount })}</span>
      {view.changed && <ChangedBadge />}
    </div>
  )
}

function LastChange({ change }: { change: LastChangeView }) {
  const { t, i18n } = useTranslation()
  const none = t(`${HEADER}.noValue`)
  const what =
    change.kind === 'property_changed'
      ? t(`${HEADER}.${change.pinned ? 'propertyPinned' : 'propertyChange'}`, {
          key: change.key,
          old: change.old ?? none,
          new: change.new ?? none,
        })
      : t(`investigations.entityDetail.history.kinds.${change.kind ?? 'unknown'}`)
  const source =
    change.kind === 'property_changed' && change.documentName
      ? t(`${HEADER}.fromDocument`, { document: change.documentName })
      : null
  return (
    <p className="text-foreground/85">
      <span className="text-muted-foreground">{t(`${HEADER}.lastChange`)} </span>
      <span className="break-words">{what}</span>
      {source && <span className="text-muted-foreground"> {source}</span>}
      <span className="text-muted-foreground">
        {' · '}
        <time dateTime={change.learnedAt}>{formatDate(change.learnedAt, i18n.language)}</time>
      </span>
    </p>
  )
}

function Conflicts({ conflicts }: { conflicts: readonly PropertyConflictView[] }) {
  const { t } = useTranslation()
  const none = t(`${HEADER}.noValue`)
  const unknown = t('investigations.entityDetail.unknownDocument')
  return (
    <ul aria-label={t(`${HEADER}.conflicts`)} className="flex flex-col gap-0.5">
      {conflicts.map(conflict => (
        <li
          key={conflict.key}
          data-conflict-key={conflict.key}
          className="flex items-start gap-1.5"
        >
          <TriangleAlert
            aria-label={t(`${HEADER}.conflictMark`)}
            className="mt-0.5 size-3 shrink-0 text-warning"
          />
          <span className="min-w-0 break-words text-foreground/85">
            {t(`${HEADER}.${conflict.pinned ? 'pinnedConflict' : 'conflict'}`, {
              key: conflict.key,
              value: conflict.value ?? none,
              document: conflict.documentName ?? unknown,
              previous: conflict.previous ?? none,
              previousDocument: conflict.previousDocumentName ?? unknown,
            })}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function EntityHistoryHeader({
  view,
  compact = false,
}: {
  view: EntityHistoryHeaderView
  compact?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border/40 bg-card/40 px-2 py-2 text-2xs">
      <Seen view={view} />
      {!compact && view.activity.length > 0 && (
        <div className="space-y-1">
          <p className="text-muted-foreground">{t(`${HEADER}.activityLabel`)}</p>
          <ActivitySparkline months={view.activity} />
        </div>
      )}
      {view.lastChange && <LastChange change={view.lastChange} />}
      {view.conflicts.length > 0 && <Conflicts conflicts={view.conflicts} />}
    </div>
  )
}
